import React, { useState } from "react";
import { postRequest } from "../../utils/httpRequest";
import { colors, pageHeaderStyles } from "../AgenticPaymentAirdrop/styles";

const label = { display: "block", fontSize: "13px", fontWeight: 600, color: "#555", marginBottom: "6px" };
const input = {
  width: "100%", padding: "10px 12px", border: "1px solid #ccc", borderRadius: "6px",
  fontSize: "14px", boxSizing: "border-box", fontFamily: "monospace",
};
const th = {
  textAlign: "left", padding: "8px 12px", borderBottom: `2px solid ${colors.border}`,
  fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px", color: colors.gray600,
};
const td = { padding: "8px 12px", borderBottom: `1px solid ${colors.gray200 || "#eee"}`, fontSize: "13px", fontFamily: "monospace" };

const badge = (bg, color, border) => ({
  display: "inline-block", padding: "3px 10px", borderRadius: "12px",
  fontSize: "12px", fontWeight: 700, background: bg, color, border: `1px solid ${border}`, whiteSpace: "nowrap",
});
const yes = () => badge("#d4edda", "#155724", "#c3e6cb");
const no = () => badge("#f8d7da", "#721c24", "#f5c6cb");

const buildUrl = (pageUrl, action) => `${pageUrl.replace(/\/$/, "")}/${action}`;

// HBAR amounts (and chargedTxFee) come back in tinybars: 1 HBAR = 1e8 tinybars.
const fmtHbar = (tinybars) => {
  const n = Number(tinybars);
  if (!Number.isFinite(n)) return String(tinybars ?? "");
  return `${(n / 1e8).toFixed(8)} ℏ`;
};

const Row = ({ k, children }) => (
  <div style={{ display: "flex", gap: "12px", padding: "6px 0", borderBottom: `1px solid ${colors.gray100 || "#f2f2f2"}` }}>
    <div style={{ width: "180px", color: colors.gray600, fontSize: "13px", fontWeight: 600 }}>{k}</div>
    <div style={{ flex: 1, fontSize: "13px", color: colors.gray700, wordBreak: "break-all" }}>{children}</div>
  </div>
);

// On Hedera, a transfer row is signed: negative = this account SENT (debited),
// positive = this account RECEIVED (credited). Colour + tag the amount so the
// direction is obvious.
const isNegativeAmount = (r) => {
  const v = r.formattedAmount != null ? r.formattedAmount : r.amount;
  const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n < 0 : String(v).trim().startsWith("-");
};

// Amount: prefer the BE's friendly formattedAmount (+ symbol / tokenSymbol);
// fall back to ÷1e8 for HBAR or a tagged raw value for tokens (unknown decimals).
const renderAmount = (r, token) => {
  const sent = isNegativeAmount(r);
  let body;
  if (r.formattedAmount != null) {
    const sym = token ? (r.tokenSymbol || r.symbol || "") : "ℏ";
    body = `${r.formattedAmount}${sym ? ` ${sym}` : ""}`;
  } else if (!token) {
    body = fmtHbar(r.amount);
  } else {
    body = <span>{r.amount} <em style={{ color: "#999", fontStyle: "normal", fontSize: "11px" }}>(raw)</em></span>;
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "8px", justifyContent: "flex-end", color: sent ? "#c0392b" : "#1e8449", fontWeight: 600 }}>
      {body}
      <span style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px",
                     background: sent ? "#fdecea" : "#eafaf1", border: `1px solid ${sent ? "#f5c6cb" : "#c3e6cb"}`,
                     color: sent ? "#c0392b" : "#1e8449", borderRadius: "10px", padding: "1px 7px", whiteSpace: "nowrap" }}>
        {sent ? "sent" : "received"}
      </span>
    </span>
  );
};

// Account: show our username when known, with the 0.0.x id as a subtitle.
const renderAccount = (r) => (
  r.accountName
    ? (
      <div>
        <div style={{ fontWeight: 600, color: colors.primary, fontFamily: "sans-serif" }}>{r.accountName}</div>
        <div style={{ fontSize: "11px", color: colors.gray500 }}>{r.account}</div>
      </div>
    )
    : r.account
);

// Token: show symbol/name when known, with the token id as a subtitle.
const renderToken = (r) => {
  const name = r.tokenSymbol || r.tokenName;
  return name
    ? (
      <div>
        <div style={{ fontWeight: 600, fontFamily: "sans-serif" }}>{name}</div>
        <div style={{ fontSize: "11px", color: colors.gray500 }}>{r.token_id}</div>
      </div>
    )
    : r.token_id;
};

const TransferTable = ({ title, rows, token }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div style={{ marginTop: "14px" }}>
      <div style={{ fontSize: "13px", fontWeight: 700, color: colors.primary, marginBottom: "6px" }}>{title}</div>
      <div style={{ overflowX: "auto", border: `1px solid ${colors.border}`, borderRadius: "8px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: token ? "460px" : "340px" }}>
          <thead>
            <tr>
              <th style={th}>Account</th>
              {token && <th style={th}>Token</th>}
              <th style={{ ...th, textAlign: "right" }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={td}>{renderAccount(r)}</td>
                {token && <td style={td}>{renderToken(r)}</td>}
                <td style={{ ...td, textAlign: "right" }}>{renderAmount(r, token)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TransactionVerification = ({ pageUrl = "/admin/verify_transaction" }) => {
  const [transactionId, setTransactionId] = useState("");
  const [expectedRecipient, setExpectedRecipient] = useState("");
  const [expectedAmount, setExpectedAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const verify = async () => {
    const tx = transactionId.trim();
    if (!tx) { setError("Enter a transaction id or hash."); return; }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await postRequest(buildUrl(pageUrl, "verify"), {
        transactionId: tx,
        expectedRecipient: expectedRecipient.trim(),
        expectedAmount: expectedAmount.trim(),
      });
      if (res && res.success) {
        setResult(res.data || {});
      } else {
        setError((res && res.error) || "Verification failed.");
      }
    } catch (err) {
      setError(err?.data?.error || err?.data?.message || "Verification failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const r = result;
  const statusBadge = r && (
    r.verified
      ? <span style={badge("#d4edda", "#155724", "#c3e6cb")}>✓ Verified</span>
      : r.found
        ? <span style={badge("#fff3cd", "#856404", "#ffeeba")}>⚠ Found, not verified</span>
        : <span style={badge("#e9ecef", "#6c757d", "#dee2e6")}>Not found on-chain</span>
  );

  return (
    <div style={{ paddingBottom: "24px", maxWidth: "820px" }}>
      <div style={{ ...pageHeaderStyles.container, marginBottom: "20px", borderRadius: "12px" }}>
        <h1 style={pageHeaderStyles.title}>Verify Transaction</h1>
        <div style={pageHeaderStyles.infoBox}>
          <span style={pageHeaderStyles.infoLabel}>Source</span>
          <code style={pageHeaderStyles.infoValue}>Hedera mirror node</code>
        </div>
      </div>

      <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderRadius: "12px", padding: "20px", marginBottom: "20px" }}>
        <div style={{ marginBottom: "14px" }}>
          <label style={label}>Transaction ID or hash <span style={{ color: "#c00" }}>*</span></label>
          <input style={input} value={transactionId} onChange={(e) => setTransactionId(e.target.value)}
                 placeholder="0.0.1234-1700000000-000000000" disabled={loading} />
        </div>
        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "16px" }}>
          <div style={{ flex: "1 1 240px" }}>
            <label style={label}>Expected recipient (optional)</label>
            <input style={input} value={expectedRecipient} onChange={(e) => setExpectedRecipient(e.target.value)}
                   placeholder="0.0.7119522" disabled={loading} />
          </div>
          <div style={{ flex: "1 1 160px" }}>
            <label style={label}>Expected amount (optional)</label>
            <input style={input} value={expectedAmount} onChange={(e) => setExpectedAmount(e.target.value)}
                   placeholder="9.982203" disabled={loading} />
            <div style={{ fontSize: "11px", color: colors.gray500, marginTop: "4px" }}>
              Human amount (e.g. 9.982203), not the raw on-chain integer.
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={verify}
          disabled={loading}
          style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center", minHeight: "40px",
            background: colors.primary, color: "#fff", border: "none", borderRadius: "6px",
            padding: "10px 22px", fontSize: "14px", fontWeight: 700,
            cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? "Verifying…" : "Verify on blockchain"}
        </button>

        {error && (
          <div style={{ background: "#f8d7da", color: "#721c24", border: "1px solid #f5c6cb", borderRadius: "6px", padding: "10px 12px", fontSize: "13px", marginTop: "14px" }}>
            {error}
          </div>
        )}
      </div>

      {r && (
        <div style={{ background: colors.white, border: `1px solid ${colors.border}`, borderRadius: "12px", padding: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "16px", fontWeight: 700, color: colors.primary }}>Result</span>
              {statusBadge}
            </div>
            {r.explorerUrl && (
              <a href={r.explorerUrl} target="_blank" rel="noopener noreferrer"
                 style={{ ...badge("#2e3b61", "#fff", "#2e3b61"), textDecoration: "none", padding: "8px 14px", fontSize: "13px" }}>
                View on HashScan ↗
              </a>
            )}
          </div>

          {!r.found && r.reason && (
            <div style={{ background: "#fff3cd", color: "#856404", border: "1px solid #ffeeba", borderRadius: "6px", padding: "10px 12px", fontSize: "13px", marginBottom: "12px" }}>
              {r.reason}
            </div>
          )}

          <Row k="Found on mirror node">{r.found ? <span style={yes()}>Yes</span> : <span style={no()}>No</span>}</Row>
          {r.result != null && <Row k="On-chain result">{r.result}</Row>}
          {r.network && <Row k="Network">{r.network}</Row>}
          {r.consensusTimestamp && <Row k="Consensus timestamp">{r.consensusTimestamp}</Row>}
          {(r.chargedFeeHbar != null || r.chargedTxFee != null) && (
            <Row k="Charged fee">
              {r.chargedFeeHbar != null ? `${r.chargedFeeHbar} ℏ` : fmtHbar(r.chargedTxFee)}
            </Row>
          )}

          {r.checks && (
            <Row k="Expectation checks">
              <span style={{ display: "inline-flex", gap: "8px", flexWrap: "wrap" }}>
                <span style={r.checks.recipientMatch ? yes() : no()}>
                  recipient {r.checks.recipientMatch ? "match" : "mismatch"}
                </span>
                <span style={r.checks.amountMatch ? yes() : no()}>
                  amount {r.checks.amountMatch ? "match" : "mismatch"}
                </span>
              </span>
            </Row>
          )}

          <TransferTable title="HBAR transfers" rows={r.transfers} />
          <TransferTable title="Token transfers" rows={r.tokenTransfers} token />
        </div>
      )}
    </div>
  );
};

export default TransactionVerification;
